import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Checkbox,
  Spin,
  message,
  Button,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Input,
  Card,
  Divider,
} from "antd";
import {
  SaveOutlined,
  CloseOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { usePermissions } from "../../../permissions";

const { Text } = Typography;

const RolePermissions = ({ visible, onCancel, role, apiUrl }) => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { editPermission } = usePermissions();
  const isEditable = editPermission;

  useEffect(() => {
    if (visible && role) {
      fetchPermissions();
    }
  }, [visible, role]);

  useEffect(() => {
    // Filter permissions based on search text
    if (searchText) {
      const filtered = permissions.filter((p) =>
        p.name?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [searchText, permissions]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/roles/${role.id}/permissions`);
      setPermissions(data);
      setFilteredPermissions(data);
    } catch (err) {
      message.error("Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (permissionId, field, checked) => {
    if (!isEditable) return;
    setPermissions((prev) =>
      prev.map((p) =>
        p.id === permissionId ? { ...p, [field]: checked } : p
      )
    );
  };

  const handleSelectAll = (permissionId, checked) => {
    if (!isEditable) return;
    setPermissions((prev) =>
      prev.map((p) =>
        p.id === permissionId
          ? {
              ...p,
              create_permission: checked,
              view_permission: checked,
              edit_permission: checked,
              delete_permission: checked,
            }
          : p
      )
    );
  };

  const handleBulkSelect = (operation, checked) => {
    if (!isEditable) return;
    setPermissions((prev) =>
      prev.map((p) => ({
        ...p,
        [operation]: checked,
      }))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${role.id}/permissions`, { permissions });
      message.success("Permissions updated successfully");
      onCancel();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  // Calculate statistics based on filtered permissions
  const totalPermissions = filteredPermissions.length;
  const enabledCreate = filteredPermissions.filter((p) => p.create_permission).length;
  const enabledView = filteredPermissions.filter((p) => p.view_permission).length;
  const enabledEdit = filteredPermissions.filter((p) => p.edit_permission).length;
  const enabledDelete = filteredPermissions.filter((p) => p.delete_permission).length;

  const columns = [
    {
      title: "Module / Permission",
      dataIndex: "name",
      key: "name",
      width: 250,
      fixed: "left",
      render: (text) => (
        <Space>
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Create",
      dataIndex: "create_permission",
      key: "create",
      align: "center",
      width: 100,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          disabled={!isEditable}
          onChange={(e) =>
            handleCheckboxChange(record.id, "create_permission", e.target.checked)
          }
        />
      ),
    },
    {
      title: "View",
      dataIndex: "view_permission",
      key: "view",
      align: "center",
      width: 100,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          disabled={!isEditable}
          onChange={(e) =>
            handleCheckboxChange(record.id, "view_permission", e.target.checked)
          }
        />
      ),
    },
    {
      title: "Edit",
      dataIndex: "edit_permission",
      key: "edit",
      align: "center",
      width: 100,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          disabled={!isEditable}
          onChange={(e) =>
            handleCheckboxChange(record.id, "edit_permission", e.target.checked)
          }
        />
      ),
    },
    {
      title: "Delete",
      dataIndex: "delete_permission",
      key: "delete",
      align: "center",
      width: 100,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          disabled={!isEditable}
          onChange={(e) =>
            handleCheckboxChange(record.id, "delete_permission", e.target.checked)
          }
        />
      ),
    },
    {
      title: "All",
      key: "all",
      align: "center",
      width: 80,
      render: (_, record) => (
        <Checkbox
          disabled={!isEditable}
          onChange={(e) => handleSelectAll(record.id, e.target.checked)}
          checked={
            record.create_permission &&
            record.view_permission &&
            record.edit_permission &&
            record.delete_permission
          }
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <span>Permissions for:</span>
          <Tag color="gold" style={{ fontSize: 16, padding: "4px 12px" }}>
            {role?.name}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={
        isEditable ? (
          <Space>
            <Button icon={<CloseOutlined />} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Permissions
            </Button>
          </Space>
        ) : (
          <Button onClick={onCancel}>Close</Button>
        )
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* Statistics Cards */}
        <Divider/>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            {/* <Card size="small"> */}
              <Statistic
                title="Total Modules"
                value={totalPermissions}
                // valueStyle={{ color: "#1677ff" }}
              />
            {/* </Card> */}
          </Col>
          <Col span={4}>
            {/* <Card size="small"> */}
              <Statistic
                title="Create Enabled"
                value={enabledCreate}
                // valueStyle={{ color: "#52c41a" }}
              />
            {/* </Card> */}
          </Col>
          <Col span={4}>
            {/* <Card size="small"> */}
              <Statistic
                title="View Enabled"
                value={enabledView}
                // valueStyle={{ color: "#1890ff" }}
              />
            {/* </Card> */}
          </Col>
          <Col span={4}>
            {/* <Card size="small"> */}
              <Statistic
                title="Edit Enabled"
                value={enabledEdit}
                // valueStyle={{ color: "#faad14" }}
              />
            {/* </Card> */}
          </Col>
          <Col span={4}>
            {/* <Card size="small"> */}
              <Statistic
                title="Delete Enabled"
                value={enabledDelete}
                // valueStyle={{ color: "#f5222d" }}
              />
            {/* </Card> */}
          </Col>
        </Row>

        <Divider/>

        {/* Search and Bulk Actions Bar */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Space>
            <Input.Search
              placeholder="Search modules..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            {searchText && (
              <Button
                size="small"
                onClick={() => setSearchText("")}
                icon={<ClearOutlined />}
              >
                Clear
              </Button>
            )}
          </Space>

          {isEditable && totalPermissions > 0 && (
            <Space>
              <Button
                size="small"
                onClick={() => handleBulkSelect("create_permission", true)}
              >
                Enable All Create
              </Button>
              <Button
                size="small"
                onClick={() => handleBulkSelect("create_permission", false)}
              >
                Disable All Create
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  const allChecked = permissions.every(
                    (p) =>
                      p.create_permission &&
                      p.view_permission &&
                      p.edit_permission &&
                      p.delete_permission
                  );
                  permissions.forEach((p) => {
                    handleSelectAll(p.id, !allChecked);
                  });
                }}
              >
                {permissions.every(
                  (p) =>
                    p.create_permission &&
                    p.view_permission &&
                    p.edit_permission &&
                    p.delete_permission
                )
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Space>
          )}
        </div>

        {/* Permissions Table */}
        <Table
          columns={columns}
          dataSource={filteredPermissions}
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
          scroll={{ y: 400 }}
          bordered
        />

        {/* Legend */}
        <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Legend:</strong> Create = Add new records | View = Read records |
            Edit = Update existing records | Delete = Remove records
          </Text>
        </div>
      </Spin>
    </Modal>
  );
};

export default RolePermissions;